/**
 * frontend/src/views/ResultView.ts
 * ==================================
 * Vista 4 — Pantalla de resultado (éxito, cancelación o error).
 * Corresponde a Sub-issues 2 y 4 de Epic 6.
 *
 * Renderiza el estado final de la sesión con feedback claro al usuario.
 * Usa errorMessages.ts para clasificar errores y mostrar mensajes accionables.
 */

import type { AppState, Vista } from "../types";
import { classifyErrors } from "../utils/errorMessages";

import { escapeHtml as _escape } from "../utils/texto";
import { icon } from "../utils/icons";

export function renderResultView(
  container: HTMLElement,
  state: AppState,
  vista: Vista,
  onNuevaConciliacion: () => void
): void {
  container.innerHTML = "";

  const wrapper = document.createElement("div");
  wrapper.className = "result-view";

  if (vista === "completado") {
    wrapper.innerHTML = `
      <header class="result-view__header">
      <div class="result-view__icon result-view__icon--ok" aria-label="Éxito">${icon("check")}</div>
      <h2 class="result-view__title">Reporte enviado</h2>
      </header>
      <div class="result-view__body">
      ${state.periodoLabel ? `<p class="result-view__periodo">Período <strong>${_escape(state.periodoLabel)}</strong></p>` : ""}
      <p class="result-view__hint">Los reportes fueron generados y enviados por email a los destinatarios configurados.</p>
      </div>
    `;

  } else if (vista === "cancelado") {
    wrapper.innerHTML = `
      <header class="result-view__header">
      <div class="result-view__icon result-view__icon--cancel" aria-label="No enviado">${icon("close")}</div>
      <h2 class="result-view__title">Reporte no enviado</h2>
      </header>
      <div class="result-view__body">
      <p class="result-view__hint">
        No se envió nada por correo. Si descargó el Excel o el PDF, ya los tiene en su equipo.
      </p>
      </div>
    `;

  } else {
    // El detalle interno se clasifica, pero nunca se imprime en la interfaz.
    const errores = state.errores.length > 0
      ? state.errores
      : ["Ocurrió un error inesperado sin detalles disponibles."];

    const info = classifyErrors(errores);

    wrapper.innerHTML = `
      <header class="result-view__header">
      <div class="result-view__icon result-view__icon--error" aria-label="Error">${icon("alert")}</div>
      <h2 class="result-view__title">${_escape(info.title)}</h2>
      </header>
      <div class="result-view__body">

      <div class="result-view__error-card">
        <p class="result-view__error-detail">${_escape(info.detail)}</p>

        <div class="result-view__error-suggestion">
          <span class="result-view__suggestion-label">¿Qué hacer?</span>
          <p>${_escape(info.suggestion)}</p>
        </div>

      </div>
      </div>
    `;
  }

  // Botón nueva conciliación (siempre presente)
  const btnNueva = document.createElement("button");
  btnNueva.className = "btn btn--primary result-view__btn-nueva";
  btnNueva.type = "button";
  btnNueva.innerHTML = `Nueva conciliación ${icon("arrow")}`;
  btnNueva.addEventListener("click", onNuevaConciliacion);
  const footer = document.createElement("div");
  footer.className = "result-view__footer";
  footer.appendChild(btnNueva);
  wrapper.appendChild(footer);

  container.appendChild(wrapper);
}
